/**
 * Recurring transactions engine (`recurring_rule` table) — decision D5:
 * CLIENT-side engine, local-first.
 *
 * Model: each rule stores `template_json` (TxTemplatePayload), a frequency,
 * and a `next_run_on` pointer. The engine materializes due txs upon opening the
 * app or returning to foreground (multi-occurrence catch-up) and advances the pointer.
 *
 * Multi-device idempotence: the id of each generated tx is a deterministic
 * UUID v5 from `(rule_id + date YYYY-MM-DD)` under a fixed namespace. Two
 * offline devices materializing the same occurrence produce the SAME
 * id, and PowerSync write-back upserts on the server without duplication.
 *
 * Additional determinism:
 *   - `occurred_at` of generated tx = UTC noon of occurrence day
 *     (`T12:00:00.000Z`): same string on all devices and local date does
 *     not shift across ±11h timezones.
 *   - `base_amount = amount` and `base_currency = currency` from template: using
 *     exchange rates would break determinism (each device would have a different
 *     rate). If currency differs from base, user can edit the generated tx.
 */
import { and, asc, eq, type PowerSyncDrizzleDB, recurring_rule, tx } from "@cashsync/powersync";
import { nowISO, todayDateKey } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { createTransactionMutation, type TransactionDraft } from "./mutations";

export type RecurringFrequency = "daily" | "weekly" | "biweekly" | "monthly" | "yearly";

export const RECURRING_FREQUENCIES: readonly RecurringFrequency[] = [
  "daily",
  "weekly",
  "biweekly",
  "monthly",
  "yearly",
];

export function isRecurringFrequency(value: string): value is RecurringFrequency {
  return (RECURRING_FREQUENCIES as readonly string[]).includes(value);
}

export interface RecurringRuleRow {
  id: string;
  frequency: RecurringFrequency;
  interval: number;
  next_run_on: string;
  end_on: string | null;
  last_run_on: string | null;
  active: boolean;
  /** JSON de TxTemplatePayload. */
  template_json: string;
}

/** Template payload shared with `tx_template` (amounts in minor units). */
export interface TxTemplatePayload {
  kind: "expense" | "income" | "transfer";
  amount: number;
  currency: string;
  walletId: string;
  toWalletId?: string | null;
  toAmount?: number | null;
  feeAmount?: number | null;
  categoryId?: string | null;
  note?: string | null;
  payee?: string | null;
  projectId?: string | null;
}

export interface RecurringRuleDraft {
  frequency: RecurringFrequency;
  interval?: number;
  nextRunOn: string;
  endOn?: string | null;
  template: TxTemplatePayload;
  active?: boolean;
}

/** Active rules with `next_run_on` past due (candidates to materialize). */
export function getActiveRecurringRulesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recurring_rule)
    .where(and(eq(recurring_rule.user_id, userId), eq(recurring_rule.active, 1)))
    .orderBy(asc(recurring_rule.next_run_on));
}

/** All user rules (/recurring view, includes inactive). */
export function getRecurringRulesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(recurring_rule)
    .where(eq(recurring_rule.user_id, userId))
    .orderBy(asc(recurring_rule.next_run_on), asc(recurring_rule.created_at));
}

// ─── UUID v5 (RFC 4122, SHA-1) sin dependencias ───────────────────────────────

/**
 * Fixed CashSync namespace for txs generated by recurring rules.
 * DO NOT change: would alter already materialized deterministic IDs.
 */
export const RECURRING_TX_NAMESPACE = "5e0fcdd7-25b1-4d21-a0e8-339bdc1c2f43";

const UUID_BYTE_LENGTH = 16;
const HEX_RADIX = 16;

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replaceAll("-", "");
  if (hex.length !== UUID_BYTE_LENGTH * 2) {
    throw new Error(`Invalid UUID: ${uuid}`);
  }
  const bytes = new Uint8Array(UUID_BYTE_LENGTH);
  for (let i = 0; i < UUID_BYTE_LENGTH; i += 1) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), HEX_RADIX);
  }
  return bytes;
}

function bytesToUuid(bytes: Uint8Array): string {
  const hex = [...bytes].map((b) => b.toString(HEX_RADIX).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * UUID v5 (SHA-1 of namespace + name) using Web Crypto. Async because
 * `crypto.subtle.digest` is async; available in browser, Tauri WebView and Bun.
 */
export async function uuidV5(name: string, namespace: string): Promise<string> {
  const namespaceBytes = uuidToBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);
  const input = new Uint8Array(namespaceBytes.length + nameBytes.length);
  input.set(namespaceBytes, 0);
  input.set(nameBytes, namespaceBytes.length);

  const digest = new Uint8Array(await crypto.subtle.digest("SHA-1", input));
  const bytes = digest.slice(0, UUID_BYTE_LENGTH);
  // Version 5 (high bits of byte 6) and RFC 4122 variant (byte 8).
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x50;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  return bytesToUuid(bytes);
}

/** Deterministic id of the tx materialized for `ruleId` on `dateKey` (YYYY-MM-DD). */
export async function generateRecurringTxId(ruleId: string, dateKey: string): Promise<string> {
  return uuidV5(`${ruleId}:${dateKey}`, RECURRING_TX_NAMESPACE);
}

// ─── Date arithmetic (pure) ──────────────────────────────────────────────────

const ISO_DATE_LENGTH = 10;
const MONTHS_PER_YEAR = 12;

function daysInMonth(year: number, monthZeroBased: number): number {
  return new Date(Date.UTC(year, monthZeroBased + 1, 0)).getUTCDate();
}

function formatDateKey(year: number, monthZeroBased: number, day: number): string {
  const mm = String(monthZeroBased + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/**
 * Advances a YYYY-MM-DD date according to frequency and interval.
 *
 * Monthly/yearly: clamp to end of month (Jan 31 → Feb 28/29). `anchorDay` allows
 * preserving the original anchor day across a series (Feb 28 with anchor 31
 * → Mar 31); if omitted, the day from `dateKey` is used.
 */
export function advanceRunDate(
  dateKey: string,
  frequency: RecurringFrequency,
  interval: number,
  anchorDay?: number,
): string {
  const [yRaw, mRaw, dRaw] = dateKey.split("-");
  const year = Number(yRaw);
  const month = Number(mRaw) - 1;
  const day = Number(dRaw);
  const step = Math.max(1, Math.trunc(interval) || 1);

  const DAYS: Partial<Record<RecurringFrequency, number>> = {
    daily: 1,
    weekly: 7,
    biweekly: 14,
  };
  const days = DAYS[frequency];
  if (days !== undefined) {
    const d = new Date(Date.UTC(year, month, day + days * step));
    return d.toISOString().slice(0, ISO_DATE_LENGTH);
  }

  const targetDay = anchorDay ?? day;
  if (frequency === "monthly") {
    const monthsTotal = month + step;
    const targetYear = year + Math.floor(monthsTotal / MONTHS_PER_YEAR);
    const targetMonth = monthsTotal % MONTHS_PER_YEAR;
    return formatDateKey(
      targetYear,
      targetMonth,
      Math.min(targetDay, daysInMonth(targetYear, targetMonth)),
    );
  }

  // yearly — clamp for Feb 29 in non-leap years.
  const targetYear = year + step;
  return formatDateKey(targetYear, month, Math.min(targetDay, daysInMonth(targetYear, month)));
}

// ─── Catch-up de ocurrencias (puro) ───────────────────────────────────────────

/** Safety cap per rule and run (≈ 1 year of daily frequency). */
export const MAX_CATCHUP_OCCURRENCES = 366;

export interface DueOccurrencesInput {
  frequency: RecurringFrequency;
  interval: number;
  next_run_on: string;
  end_on: string | null;
  active: boolean;
}

export interface DueOccurrencesResult {
  /** Fechas YYYY-MM-DD vencidas, en orden ascendente. */
  occurrences: string[];
  /** Nuevo puntero `next_run_on` (primera fecha futura). */
  nextRunOn: string;
  /** true when `end_on` is in the past: rule can be deactivated. */
  finished: boolean;
}

/**
 * Due occurrences of a rule up to `todayKey` inclusive (multi-occurrence
 * catch-up). `end_on` is inclusive. Monthly/yearly anchor day is taken from
 * input `next_run_on` and preserved throughout this execution's series
 * (Jan 31 → Feb 28 → Mar 31); once clamped pointer is persisted, the anchor
 * becomes the clamped day (documented limitation: schema does not store
 * original anchor day).
 */
export function computeDueOccurrences(
  rule: DueOccurrencesInput,
  todayKey: string,
  maxOccurrences = MAX_CATCHUP_OCCURRENCES,
): DueOccurrencesResult {
  if (!rule.active) {
    return { occurrences: [], nextRunOn: rule.next_run_on, finished: false };
  }

  const anchorDay = Number(rule.next_run_on.slice(8, 10));
  const occurrences: string[] = [];
  let cursor = rule.next_run_on;

  while (
    cursor <= todayKey &&
    (rule.end_on === null || cursor <= rule.end_on) &&
    occurrences.length < maxOccurrences
  ) {
    occurrences.push(cursor);
    cursor = advanceRunDate(cursor, rule.frequency, rule.interval, anchorDay);
  }

  const finished = rule.end_on !== null && cursor > rule.end_on;
  return { occurrences, nextRunOn: cursor, finished };
}

// ─── Template (validation on parse) ──────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Parsea y valida `template_json`. Lanza si el payload no es materializable. */
export function parseTxTemplate(json: string): TxTemplatePayload {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new Error("invalid template_json: malformed JSON");
  }
  if (!isRecord(raw)) {
    throw new Error("invalid template_json: not an object");
  }
  const obj = raw;

  const { kind } = obj;
  if (kind !== "expense" && kind !== "income" && kind !== "transfer") {
    throw new Error("invalid template_json: unknown kind");
  }
  const { amount } = obj;
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0) {
    throw new Error("invalid template_json: amount must be an integer ≥ 0");
  }
  const currency = optionalString(obj["currency"]);
  if (!currency) {
    throw new Error("invalid template_json: currency required");
  }
  const walletId = optionalString(obj["walletId"]);
  if (!walletId) {
    throw new Error("invalid template_json: walletId required");
  }
  const toWalletId = optionalString(obj["toWalletId"]);
  if (kind === "transfer" && !toWalletId) {
    throw new Error("invalid template_json: transfer requires toWalletId");
  }

  return {
    kind,
    amount: Math.round(amount),
    currency,
    walletId,
    toWalletId,
    toAmount: optionalNumber(obj["toAmount"]),
    feeAmount: optionalNumber(obj["feeAmount"]),
    categoryId: optionalString(obj["categoryId"]),
    note: optionalString(obj["note"]),
    payee: optionalString(obj["payee"]),
    projectId: optionalString(obj["projectId"]),
  };
}

/** Deterministic `occurred_at` for an occurrence: UTC noon (see header). */
export function occurrenceISO(dateKey: string): string {
  return `${dateKey}T12:00:00.000Z`;
}

/** Transaction draft to materialize for a specific occurrence. */
export function buildRecurringTransactionDraft(
  template: TxTemplatePayload,
  ruleId: string,
  dateKey: string,
  id: string,
): TransactionDraft {
  return {
    id,
    kind: template.kind,
    amount: template.amount,
    currency: template.currency,
    // Multi-device determinism: no FX conversion (see header).
    baseAmount: template.amount,
    baseCurrency: template.currency,
    walletId: template.walletId,
    toWalletId: template.toWalletId ?? null,
    feeAmount: template.feeAmount ?? null,
    categoryId: template.categoryId ?? null,
    occurredAtISO: occurrenceISO(dateKey),
    note: template.note ?? null,
    payee: template.payee ?? null,
    projectId: template.projectId ?? null,
    recurringRuleId: ruleId,
  };
}

// ─── Orchestrator (PowerSync local write) ────────────────────────────────────

/**
 * Materializes all due occurrences of active user rules and
 * advances their pointers. Idempotent: if deterministic tx already exists
 * locally (another device synced it) it is skipped; if two devices
 * generate it offline simultaneously, the server upserts it by id.
 *
 * Returns number of created transactions.
 */
export async function runRecurringRules(
  db: PowerSyncDrizzleDB,
  userId: string,
  todayKey: string = todayDateKey(),
  /**
   * CONFIG_OVER_CODE: occurrence cap per rule and run. Default is
   * historical literal. Bounds HOW MANY occurrences materialize at once
   * after months without opening app; does not alter amount of any.
   */
  maxOccurrences: number = MAX_CATCHUP_OCCURRENCES,
  options?: VerifiedWriteOptions,
): Promise<number> {
  const rules = await getActiveRecurringRulesQuery(db, userId);
  let created = 0;

  for (const rule of rules) {
    if (!rule.next_run_on || !rule.frequency || !isRecurringFrequency(rule.frequency)) {
      continue;
    }
    let template: TxTemplatePayload;
    try {
      template = parseTxTemplate(rule.template_json ?? "");
    } catch (err) {
      console.warn(`[recurring] rule ${rule.id} with invalid template:`, err);
      continue;
    }

    const { occurrences, nextRunOn, finished } = computeDueOccurrences(
      {
        frequency: rule.frequency,
        interval: rule.interval ?? 1,
        next_run_on: rule.next_run_on,
        end_on: rule.end_on ?? null,
        active: rule.active === 1,
      },
      todayKey,
      maxOccurrences,
    );

    for (const dateKey of occurrences) {
      const id = await generateRecurringTxId(rule.id, dateKey);
      const existing = await db.select({ id: tx.id }).from(tx).where(eq(tx.id, id)).limit(1);
      if (existing.length > 0) {
        continue;
      }
      await createTransactionMutation(
        db,
        userId,
        buildRecurringTransactionDraft(template, rule.id, dateKey, id),
      );
      created += 1;
    }

    if (occurrences.length > 0 || finished) {
      // Verified: if rule was created and due in the same session (past start
      // date), PowerSync window could match zero rows and leave pointer
      // unadvanced — next run would regenerate the SAME occurrences that
      // `existing` already filters by id, so no duplicate txs, but rule would
      // loop materializing the same batch.
      await patchRowVerified(
        db,
        recurring_rule,
        recurring_rule.id,
        rule.id,
        {
          next_run_on: nextRunOn,
          last_run_on: occurrences.at(-1) ?? rule.last_run_on,
          active: finished ? 0 : 1,
        },
        options,
      );
    }
  }

  return created;
}

// ─── Mutaciones de reglas ─────────────────────────────────────────────────────

export async function createRecurringRuleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: RecurringRuleDraft,
): Promise<string> {
  // Validates template by serializing it through the same path as the engine.
  const templateJson = JSON.stringify(draft.template);
  parseTxTemplate(templateJson);

  const id = generateId();
  const now = nowISO();
  await db.insert(recurring_rule).values({
    id,
    user_id: userId,
    frequency: draft.frequency,
    interval: Math.max(1, Math.trunc(draft.interval ?? 1)),
    next_run_on: draft.nextRunOn,
    end_on: draft.endOn ?? null,
    last_run_on: null,
    active: (draft.active ?? true) ? 1 : 0,
    template_json: templateJson,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateRecurringRuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    frequency: RecurringFrequency;
    interval: number;
    next_run_on: string;
    end_on: string | null;
    active: number;
    template_json: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  if (updates.template_json !== undefined) {
    parseTxTemplate(updates.template_json);
  }
  await patchRowVerified(db, recurring_rule, recurring_rule.id, id, updates, options);
}

/**
 * PHYSICAL deletion of rule. Already materialized txs keep their
 * `recurring_rule_id` as historical reference (no server FK).
 */
export async function deleteRecurringRuleMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(recurring_rule).where(eq(recurring_rule.id, id));
}
